import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Home, 
  CreditCard, 
  Gift, 
  User, 
  Bell,
  TrendingUp,
  ArrowUpRight,
  ArrowDownLeft,
  ChevronRight,
  Fuel,
  Cpu,
  Building2,
  Star,
  LogOut,
  Lock,
  Unlock,
  Settings
} from 'lucide-react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../context/AuthContext';
import { mockPoints, mockTransactions, mockOffers, mockNotifications } from '../../data/appMock';
import { toast } from 'sonner';

const categoryIcons = {
  carburant: Fuel,
  technologie: Cpu,
  immobilier: Building2,
  recompense: Gift
};

const categoryColors = {
  carburant: '#FFD700',
  technologie: '#0066CC',
  immobilier: '#00C853',
  recompense: '#6B2C91'
};

const AppDashboard = () => {
  const [activeTab, setActiveTab] = useState('home');
  const [cardBlocked, setCardBlocked] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const unreadNotifications = mockNotifications.filter(n => !n.read).length;

  const handleLogout = () => {
    logout();
    navigate('/app/login');
    toast.success('Déconnexion réussie');
  };

  const toggleCardBlock = () => {
    setCardBlocked(!cardBlocked);
    toast.success(cardBlocked ? 'Carte débloquée' : 'Carte bloquée temporairement');
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'home':
        return <HomeTab user={user} />;
      case 'card':
        return <CardTab user={user} cardBlocked={cardBlocked} toggleCardBlock={toggleCardBlock} />;
      case 'offers':
        return <OffersTab />;
      case 'profile':
        return <ProfileTab user={user} handleLogout={handleLogout} />;
      default:
        return <HomeTab user={user} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#6B2C91] to-[#5a2579] text-white p-4 pt-8 pb-6 rounded-b-3xl">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <span className="font-bold text-lg">
                {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
              </span>
            </div>
            <div>
              <p className="text-white/70 text-sm">Bonjour,</p>
              <p className="font-semibold">{user?.firstName} {user?.lastName}</p>
            </div>
          </div>
          <button 
            className="relative p-2 bg-white/10 rounded-full"
            onClick={() => setActiveTab('profile')}
          >
            <Bell className="w-6 h-6" />
            {unreadNotifications > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center">
                {unreadNotifications}
              </span>
            )}
          </button>
        </div>

        {/* Points Card */}
        <Card className="bg-white/10 backdrop-blur-sm border-0 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/70 text-sm">Vos Points</p>
              <p className="text-3xl font-bold">{mockPoints.total.toLocaleString()}</p>
              <div className="flex items-center gap-1 text-sm text-[#00C853] mt-1">
                <TrendingUp className="w-4 h-4" />
                +{mockPoints.monthlyGain.toLocaleString()} ce mois
              </div>
            </div>
            <div className="text-right">
              <div className="inline-flex items-center gap-1 bg-[#FFD700] text-gray-900 px-3 py-1 rounded-full text-sm font-semibold">
                <Star className="w-4 h-4" />
                {user?.level}
              </div>
              <p className="text-white/60 text-xs mt-2">
                {mockPoints.pointsToNextLevel.toLocaleString()} pts pour {mockPoints.nextLevel}
              </p>
            </div>
          </div>
          {/* Progress bar */}
          <div className="mt-3 bg-white/20 rounded-full h-2 overflow-hidden">
            <div 
              className="bg-[#FFD700] h-full rounded-full transition-all duration-500"
              style={{ width: `${mockPoints.levelProgress}%` }}
            />
          </div>
        </Card>
      </div>

      {/* Content */}
      <div className="p-4">
        {renderContent()}
      </div>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-2 flex justify-around items-center">
        {[
          { id: 'home', icon: Home, label: 'Accueil' },
          { id: 'card', icon: CreditCard, label: 'Carte' },
          { id: 'offers', icon: Gift, label: 'Offres' },
          { id: 'profile', icon: User, label: 'Profil' }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex flex-col items-center py-2 px-4 rounded-xl transition-colors ${
              activeTab === tab.id 
                ? 'text-[#6B2C91] bg-[#6B2C91]/10' 
                : 'text-gray-500'
            }`}
          >
            <tab.icon className="w-6 h-6" />
            <span className="text-xs mt-1">{tab.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
};

// Home Tab Component
const HomeTab = ({ user }) => (
  <div className="space-y-6">
    {/* Quick Actions */}
    <div className="grid grid-cols-3 gap-3">
      {[
        { icon: Fuel, label: 'Stations', color: '#FFD700' },
        { icon: Gift, label: 'Récompenses', color: '#6B2C91' },
        { icon: CreditCard, label: 'Ma Carte', color: '#0066CC' }
      ].map((action, idx) => (
        <Card key={idx} className="p-4 text-center border-0 shadow-sm">
          <div 
            className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-2"
            style={{ backgroundColor: `${action.color}20` }}
          >
            <action.icon className="w-6 h-6" style={{ color: action.color }} />
          </div>
          <span className="text-xs text-gray-700">{action.label}</span>
        </Card>
      ))}
    </div>

    {/* Recent Transactions */}
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-bold text-gray-900">Transactions récentes</h2>
        <button className="text-[#6B2C91] text-sm font-medium">Voir tout</button>
      </div>
      <Card className="border-0 shadow-sm divide-y divide-gray-100">
        {mockTransactions.slice(0, 4).map((txn) => {
          const Icon = categoryIcons[txn.category] || Gift;
          const color = categoryColors[txn.category] || '#6B2C91';
          return (
            <div key={txn.id} className="p-4 flex items-center gap-3">
              <div 
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${color}20` }}
              >
                <Icon className="w-5 h-5" style={{ color }} />
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-900 text-sm">{txn.merchant}</p>
                <p className="text-xs text-gray-500">
                  {new Date(txn.date).toLocaleDateString('fr-FR', { 
                    day: 'numeric', 
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
              <div className="text-right">
                <p className={`font-semibold ${txn.type === 'credit' ? 'text-gray-900' : 'text-red-500'}`}>
                  {txn.amount > 0 ? '+' : ''}{txn.amount.toLocaleString()} F
                </p>
                <p className={`text-xs ${txn.points > 0 ? 'text-[#00C853]' : 'text-red-500'}`}>
                  {txn.points > 0 ? '+' : ''}{txn.points} pts
                </p>
              </div>
            </div>
          );
        })}
      </Card>
    </div>
  </div>
);

// Card Tab Component
const CardTab = ({ user, cardBlocked, toggleCardBlock }) => (
  <div className="space-y-6">
    {/* Virtual Card */}
    <div className={`relative h-52 rounded-2xl p-6 text-white overflow-hidden ${
      cardBlocked 
        ? 'bg-gradient-to-br from-gray-400 to-gray-500' 
        : 'bg-gradient-to-br from-slate-600 to-slate-700'
    }`}>
      <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent" />
      {cardBlocked && (
        <div className="absolute inset-0 bg-gray-900/50 flex items-center justify-center">
          <div className="text-center">
            <Lock className="w-12 h-12 mx-auto mb-2" />
            <p className="font-semibold">Carte Bloquée</p>
          </div>
        </div>
      )}
      <div className="relative z-10 h-full flex flex-col justify-between">
        <div className="flex justify-between items-start">
          <img
            src="/images/kipay-logo.png"
            alt="KIPAY"
            className="h-10"
          />
          <span className="bg-white/20 px-3 py-1 rounded-full text-sm font-semibold">
            {user?.level?.toUpperCase()}
          </span>
        </div>
        <div>
          <p className="text-white/70 text-xs mb-1">Numéro de carte</p>
          <p className="font-mono text-lg tracking-wider">{user?.cardNumber}</p>
        </div>
        <div className="flex justify-between items-end">
          <div>
            <p className="text-white/70 text-xs">Titulaire</p>
            <p className="font-medium">{user?.firstName} {user?.lastName}</p>
          </div>
          <div className="text-right">
            <p className="text-white/70 text-xs">Statut</p>
            <p className={`font-medium ${cardBlocked ? 'text-red-300' : 'text-[#00C853]'}`}>
              {cardBlocked ? 'Bloquée' : 'Active'}
            </p>
          </div>
        </div>
      </div>
    </div>

    {/* Card Actions */}
    <div className="grid grid-cols-2 gap-4">
      <Button
        onClick={toggleCardBlock}
        variant={cardBlocked ? "default" : "outline"}
        className={`h-14 ${cardBlocked 
          ? 'bg-[#00C853] hover:bg-[#00C853]/90 text-white' 
          : 'border-red-500 text-red-500 hover:bg-red-50'
        }`}
      >
        {cardBlocked ? (
          <>
            <Unlock className="w-5 h-5 mr-2" />
            Débloquer
          </>
        ) : (
          <>
            <Lock className="w-5 h-5 mr-2" />
            Bloquer
          </>
        )}
      </Button>
      <Button variant="outline" className="h-14 border-[#6B2C91] text-[#6B2C91]">
        <Settings className="w-5 h-5 mr-2" />
        Paramètres
      </Button>
    </div>

    {/* Card Info */}
    <Card className="p-4 border-0 shadow-sm">
      <h3 className="font-semibold text-gray-900 mb-3">Informations carte</h3>
      <div className="space-y-3 text-sm">
        <div className="flex justify-between">
          <span className="text-gray-500">Type</span>
          <span className="font-medium">KIPAY {user?.level}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Réseau</span>
          <span className="font-medium">Visa</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Membre depuis</span>
          <span className="font-medium">
            {new Date(user?.memberSince).toLocaleDateString('fr-FR', { 
              month: 'long', 
              year: 'numeric' 
            })}
          </span>
        </div>
      </div>
    </Card>
  </div>
);

// Offers Tab Component
const OffersTab = () => (
  <div className="space-y-4">
    <h2 className="font-bold text-gray-900">Offres disponibles</h2>
    {mockOffers.map((offer) => (
      <Card key={offer.id} className="p-4 border-0 shadow-sm">
        <div className="flex items-start gap-3">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
            offer.type === 'bonus' ? 'bg-[#FFD700]/20' :
            offer.type === 'discount' ? 'bg-[#00C853]/20' : 'bg-[#6B2C91]/20'
          }`}>
            {offer.type === 'bonus' && <Star className="w-6 h-6 text-[#FFD700]" />}
            {offer.type === 'discount' && <TrendingUp className="w-6 h-6 text-[#00C853]" />}
            {offer.type === 'reward' && <Gift className="w-6 h-6 text-[#6B2C91]" />}
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900">{offer.title}</h3>
            <p className="text-sm text-gray-600">{offer.description}</p>
            <p className="text-xs text-gray-400 mt-1">
              Valide jusqu'au {new Date(offer.validUntil).toLocaleDateString('fr-FR')}
            </p>
          </div>
          <ChevronRight className="w-5 h-5 text-gray-400" />
        </div>
      </Card>
    ))}
  </div>
);

// Profile Tab Component
const ProfileTab = ({ user, handleLogout }) => (
  <div className="space-y-4">
    {/* Profile Header */}
    <Card className="p-6 border-0 shadow-sm text-center">
      <div className="w-20 h-20 bg-[#6B2C91]/10 rounded-full flex items-center justify-center mx-auto mb-3">
        <span className="text-2xl font-bold text-[#6B2C91]">
          {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
        </span>
      </div>
      <h2 className="font-bold text-gray-900">{user?.firstName} {user?.lastName}</h2>
      <p className="text-gray-500 text-sm">{user?.phone}</p>
      <div className="inline-flex items-center gap-1 bg-[#FFD700]/20 text-[#FFD700] px-3 py-1 rounded-full text-sm font-semibold mt-2">
        <Star className="w-4 h-4" />
        Membre {user?.level}
      </div>
    </Card>

    {/* Notifications */}
    <Card className="border-0 shadow-sm divide-y divide-gray-100">
      <h3 className="font-semibold text-gray-900 p-4 pb-2">Notifications récentes</h3>
      {mockNotifications.map((notif) => (
        <div key={notif.id} className={`p-4 ${!notif.read ? 'bg-[#6B2C91]/5' : ''}`}>
          <div className="flex items-start gap-3">
            <div className={`w-2 h-2 rounded-full mt-2 ${!notif.read ? 'bg-[#6B2C91]' : 'bg-gray-300'}`} />
            <div>
              <p className="font-medium text-gray-900 text-sm">{notif.title}</p>
              <p className="text-xs text-gray-600">{notif.message}</p>
              <p className="text-xs text-gray-400 mt-1">
                {new Date(notif.date).toLocaleDateString('fr-FR', {
                  day: 'numeric',
                  month: 'short',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>
          </div>
        </div>
      ))}
    </Card>

    {/* Logout */}
    <Button 
      onClick={handleLogout}
      variant="outline" 
      className="w-full h-14 border-red-500 text-red-500 hover:bg-red-50"
    >
      <LogOut className="w-5 h-5 mr-2" />
      Se déconnecter
    </Button>
  </div>
);

export default AppDashboard;
